import React from 'react';
import ReactDOM from 'react-dom';
import paymentStore from 'Models/PaymentStore';
import { store } from 'Models/Store';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';

interface TestCardField {
	label: string;
	value: string;
	/** Value copied to clipboard. Falls back to `value` if not set. */
	copyValue?: string | (() => string);
}

function futureExpiry(): string {
	const d = new Date();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const year = String(d.getFullYear() + 1).slice(-2);
	return `${month}${year}`;
}

const TEST_CARDS: Record<string, TestCardField[]> = {
	STRIPE: [
		{ label: 'Card number', value: '4242 4242 4242 4242' },
		{ label: 'Expiry', value: 'Any future date', copyValue: () => futureExpiry() },
		{ label: 'CVC', value: 'Any 3 digits', copyValue: '123' },
	],
	SQUARE: [
		{ label: 'Card number', value: '4111 1111 1111 1111' },
		{ label: 'Expiry', value: 'Any future date', copyValue: () => futureExpiry() },
		{ label: 'CVC', value: '111' },
		{ label: 'Postal code', value: '94103' },
	],
};

function copyToClipboard(text: string) {
	navigator.clipboard.writeText(text.replace(/\s/g, ''));
}

function SandboxCardModal({ onClose }: { onClose: () => void }) {
	const [copied, setCopied] = React.useState<string | null>(null);
	const fields = TEST_CARDS[paymentStore.provider] ?? TEST_CARDS.STRIPE;

	const handleCopy = (field: TestCardField) => {
		const raw = field.copyValue ?? field.value;
		copyToClipboard(typeof raw === 'function' ? raw() : raw);
		setCopied(field.label);
		setTimeout(() => setCopied(null), 1500);
	};

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>Sandbox test card details</NewModal.Title>
				<Button
					className="new-modal--close"
					icon={{ icon: 'close', iconPos: 'icon-left' }}
					display={Display.Text}
					colors={Colors.Alternate}
					onClick={onClose}
					labelVisible={false}
				/>
			</NewModal.Header>
			<NewModal.Body>
				<p style={{ margin: '0 0 12px', color: '#666' }}>
					This is a test payment environment. Use the details below to complete a test payment.
				</p>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
					{fields.map(field => (
						<div
							key={field.label}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								padding: '8px 12px',
								background: '#f9f9f9',
								borderRadius: '6px',
								border: '1px solid #eee',
							}}
						>
							<div>
								<div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>
									{field.label}
								</div>
								<div style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 500 }}>
									{field.value}
								</div>
							</div>
							<button
								type="button"
								onClick={() => handleCopy(field)}
								style={{
									padding: '4px 10px',
									fontSize: '12px',
									background: '#fff',
									border: '1px solid #d9d9d9',
									borderRadius: '4px',
									cursor: 'pointer',
									whiteSpace: 'nowrap',
								}}
							>
								{copied === field.label ? 'Copied!' : 'Copy'}
							</button>
						</div>
					))}
				</div>
			</NewModal.Body>
		</>
	);
}

/**
 * Opens a modal showing sandbox test card details with copy buttons.
 */
export function openSandboxCardModal() {
	const onClose = () => store.modal.hide();

	store.modal?.show('Sandbox test card details', <SandboxCardModal onClose={onClose} />, {
		className: 'confirm-modal',
		shouldCloseOnEsc: true,
		shouldCloseOnOverlayClick: true,
		onRequestClose: onClose,
		newModal: true,
	});
}

/**
 * Portal-based tooltip that renders at document.body level to escape overflow:hidden containers.
 */
function PortalTooltip({ text, anchorRef }: { text: string; anchorRef: React.RefObject<HTMLElement> }) {
	const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

	React.useEffect(() => {
		if (anchorRef.current) {
			const rect = anchorRef.current.getBoundingClientRect();
			setPos({
				top: rect.top - 4,
				left: rect.left + rect.width / 2,
			});
		}
	}, [anchorRef]);

	if (!pos) return null;

	return ReactDOM.createPortal(
		<div
			style={{
				position: 'fixed',
				top: pos.top,
				left: pos.left,
				transform: 'translate(-50%, -100%)',
				padding: '5px 10px',
				fontSize: '11px',
				lineHeight: '1.4',
				color: '#fff',
				background: '#1f2937',
				borderRadius: '6px',
				boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
				zIndex: 9999,
				pointerEvents: 'none',
				whiteSpace: 'nowrap',
				maxWidth: '220px',
			}}
		>
			{text}
		</div>,
		document.body,
	);
}

/**
 * Small "Sandbox" pill badge that opens a modal with test card details when clicked.
 * Styled to match the capacity simulator's "Sandbox only" badge.
 * Only renders when the payment provider is in sandbox mode.
 */
export function SandboxBadge({ onClick, tooltip }: { onClick?: () => void; tooltip?: string } = {}) {
	const [showTooltip, setShowTooltip] = React.useState(false);
	const ref = React.useRef<HTMLButtonElement>(null);

	if (!paymentStore.isSandbox) {
		return null;
	}

	return (
		<button
			ref={ref}
			type="button"
			onClick={e => {
				e.stopPropagation();
				(onClick ?? openSandboxCardModal)();
			}}
			className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded bg-gray-100 text-gray-500"
			style={{ border: 'none', cursor: 'pointer', fontSize: '10px', lineHeight: '18px' }}
			onMouseEnter={() => setShowTooltip(true)}
			onMouseLeave={() => setShowTooltip(false)}
		>
			Sandbox
			<span className="material-symbols-outlined text-gray-400" style={{ fontSize: '12px', lineHeight: '1' }}>
				help
			</span>
			{tooltip && showTooltip && <PortalTooltip text={tooltip} anchorRef={ref} />}
		</button>
	);
}

/**
 * Small "Sandbox" pill badge with a hover tooltip that uses a portal to escape
 * overflow:hidden containers. Matches the capacity simulator's "Sandbox only" badge.
 * Only renders when the payment provider is in sandbox mode.
 */
export function SandboxTooltipBadge({ tooltip }: { tooltip: string }): JSX.Element | null {
	const [showTooltip, setShowTooltip] = React.useState(false);
	const ref = React.useRef<HTMLSpanElement>(null);

	if (!paymentStore.isSandbox) {
		return null;
	}

	return (
		<span
			ref={ref}
			className="inline-flex items-center gap-1 px-1.5 py-0 rounded bg-gray-100 text-gray-500"
			style={{ fontSize: '10px', lineHeight: '18px', cursor: 'default' }}
			onClick={e => e.stopPropagation()}
			onMouseEnter={() => setShowTooltip(true)}
			onMouseLeave={() => setShowTooltip(false)}
		>
			Sandbox
			<span
				className="material-symbols-outlined text-gray-400 cursor-help leading-none"
				style={{ fontSize: '12px' }}
			>
				help
			</span>
			{showTooltip && <PortalTooltip text={tooltip} anchorRef={ref} />}
		</span>
	);
}
